import React, { useEffect, useMemo, useState } from "react";
import {
  Drawer,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  MenuRoundedIcon,
  NotificationsNoneRoundedIcon,
  LogoutRoundedIcon,
  HomeOutlinedIcon,
  GroupOutlinedIcon,
  AccountTreeOutlinedIcon,
  AdminPanelSettingsOutlinedIcon,
  WidgetsOutlinedIcon,
  SecurityOutlinedIcon,
  ReceiptLongOutlinedIcon,
  CalendarOutlinedIcon,
  FolderOutlinedIcon,
} from "../../utilities/icons";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import {
  clearAuthCookies,
  getUserFromToken,
  isTokenExpiringSoon,
  refreshAuthSession,
} from "../../utilities/authUtils";

const navigation = [
  {
    section: "Workspace",
    items: [
      {
        label: "Overview",
        route: "/dashboard/home",
        icon: <HomeOutlinedIcon fontSize="small" />,
      },
      {
        label: "Users",
        route: "/dashboard/users-list",
        icon: <GroupOutlinedIcon fontSize="small" />,
      },
      {
        label: "Org Hierarchy",
        route: "/dashboard/org-hierarchy",
        icon: <AccountTreeOutlinedIcon fontSize="small" />,
      },
      {
        label: "Access Control",
        route: "/dashboard/access-management",
        icon: <AdminPanelSettingsOutlinedIcon fontSize="small" />,
      },
    ],
  },
  {
    section: "Projects",
    items: [
      {
        label: "Projects",
        route: "/dashboard/projects",
        icon: <WidgetsOutlinedIcon fontSize="small" />,
      },
      {
        label: "Roles & Permissions",
        route: "/dashboard/roles",
        icon: <SecurityOutlinedIcon fontSize="small" />,
      },
    ],
  },
  {
    section: "HR & People",
    items: [
      {
        label: "Attendance",
        route: "/dashboard/attendance",
        icon: <CalendarOutlinedIcon fontSize="small" />,
      },
      {
        label: "Documents",
        route: "/dashboard/documents",
        icon: <FolderOutlinedIcon fontSize="small" />,
      },
    ],
  },
  {
    section: "Settings",
    items: [
      {
        label: "Audit Logs",
        route: "/dashboard/audit-logs",
        icon: <ReceiptLongOutlinedIcon fontSize="small" />,
      },
    ],
  },
];

const pageMeta = {
  "/dashboard/home": {
    title: "Overview",
    subtitle: "Visibility into users, access coverage, and workspace health.",
  },
  "/dashboard/users-list": {
    title: "Users",
    subtitle: "Create, review, and manage employee identities in one place.",
  },
  "/dashboard/org-hierarchy": {
    title: "Org Hierarchy",
    subtitle: "Visualize and manage your reporting structure.",
  },
  "/dashboard/access-management": {
    title: "Access Management",
    subtitle: "Grant, review, and revoke application access with fewer clicks.",
  },
  "/dashboard/projects": {
    title: "Projects",
    subtitle:
      "Organize apps, role groups, and ownership around each workspace.",
  },
  "/dashboard/roles": {
    title: "Roles & Permissions",
    subtitle: "See role coverage across projects and standardize access rules.",
  },
  "/dashboard/audit-logs": {
    title: "Audit Logs",
    subtitle:
      "Track platform activity with structured records and full change history.",
  },
  "/dashboard/attendance": {
    title: "Attendance",
    subtitle:
      "Track employee check-ins, working hours, and attendance trends across all periods.",
  },
  "/dashboard/documents": {
    title: "Documents",
    subtitle:
      "Upload, manage, and access employee documents securely — resumes, ID proofs, certificates and more.",
  },
  "/dashboard/email-templates": {
    title: "Email Templates",
    subtitle:
      "Configure OTP email content, preview changes, and restore earlier versions.",
  },
};

function getInitials(user) {
  const first = user?.FirstName?.[0] || user?.Email?.[0] || "A";
  const second = user?.LastName?.[0] || user?.Email?.[1] || "X";
  return `${first}${second}`.toUpperCase();
}

function getDisplayName(user) {
  return (
    user?.FirstName ||
    [user?.FirstName, user?.LastName].filter(Boolean).join(" ") ||
    user?.Email?.split("@")[0] ||
    "Admin"
  );
}

function getGreeting() {
  const hour = new Date().getHours();

  if (hour < 12) {
    return "Good morning";
  }

  if (hour < 17) {
    return "Good afternoon";
  }

  return "Good evening";
}

function SidebarContent({ location, navigate, user, onNavigate }) {
  const [notificationAnchor, setNotificationAnchor] = useState(null);

  const handleLogout = () => {
    clearAuthCookies();
    navigate("/");
    onNavigate?.();
  };

  const openNotifications = (event) => {
    setNotificationAnchor(event.currentTarget);
  };

  const closeNotifications = () => {
    setNotificationAnchor(null);
  };

  return (
    <>
      <div className="ax-brand">
        <div className="ax-brand-mark">A</div>
        <div className="ax-brand-copy">
          <div className="ax-brand-name">Aapmor</div>
          <div className="ax-brand-subtitle">Identity Platform</div>
        </div>
      </div>

      <div className="ax-nav">
        {navigation.map((group) => (
          <React.Fragment key={group.section}>
            <div className="ax-nav-section">{group.section}</div>
            {group.items.map((item) => {
              const active = location.pathname === item.route;
              return (
                <button
                  key={item.label}
                  className={`ax-nav-item${active ? " ax-active" : ""} ${item.disabled ? "ax-disabled" : ""}`}
                  onClick={() => {
                    if (item.disabled) return;
                    navigate(item.route);
                    onNavigate?.();
                  }}
                  disabled={item.disabled}
                  type="button"
                  aria-label={item.label}
                >
                  <span className="ax-nav-icon">{item.icon}</span>
                  <span className="ax-nav-label">{item.label}</span>
                  {item.badge ? (
                    <span className="ax-nav-badge">{item.badge}</span>
                  ) : null}
                  {item.comingsoon && (
                    <span className="ax-nav-comingsoon">Coming Soon</span>
                  )}
                </button>
              );
            })}
          </React.Fragment>
        ))}
      </div>

      <div className="ax-sidebar-bottom">
        <Tooltip title="Notifications" placement="right" arrow>
          <button
            className="ax-nav-item ax-sidebar-action"
            onClick={openNotifications}
            type="button"
            aria-label="Notifications"
          >
            <span className="ax-nav-icon">
              <NotificationsNoneRoundedIcon fontSize="small" />
            </span>
            <span className="ax-nav-label">Notifications</span>
          </button>
        </Tooltip>

        <Tooltip title="Logout" placement="right" arrow>
          <button
            className="ax-nav-item ax-sidebar-action ax-logout-action"
            onClick={handleLogout}
            type="button"
            aria-label="Logout"
          >
            <span className="ax-nav-icon">
              <LogoutRoundedIcon fontSize="small" />
            </span>
            <span className="ax-nav-label">Logout</span>
          </button>
        </Tooltip>

        <div className="ax-sidebar-user">
          <span className="ax-nav-icon">
            <span className="ax-avatar">{getInitials(user)}</span>
          </span>
          <div className="ax-sidebar-user-info">
            <strong>{user?.FirstName || "Admin User"}</strong>
            <span>{user?.Email || user?.Role || "Super Admin"}</span>
          </div>
        </div>
      </div>

      <Menu
        anchorEl={notificationAnchor}
        open={Boolean(notificationAnchor)}
        onClose={closeNotifications}
        transformOrigin={{ horizontal: "left", vertical: "bottom" }}
        anchorOrigin={{ horizontal: "right", vertical: "center" }}
        PaperProps={{
          sx: {
            ml: 1.25,
            minWidth: 240,
            borderRadius: "12px",
            border: "1px solid var(--ax-border)",
            boxShadow: "var(--ax-shadow)",
          },
        }}
      >
        <MenuItem disabled sx={{ opacity: "1 !important", py: 1.25 }}>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <NotificationsNoneRoundedIcon
              fontSize="small"
              sx={{ color: "var(--ax-blue)" }}
            />
            <Stack spacing={0.25}>
              <Typography
                fontWeight={600}
                fontSize="0.88rem"
                color="var(--ax-ink)"
              >
                No new notifications
              </Typography>
              <Typography variant="caption" color="var(--ax-ink-2)">
                You are all caught up.
              </Typography>
            </Stack>
          </Stack>
        </MenuItem>
      </Menu>
    </>
  );
}

export default function AppShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const user = getUserFromToken();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const currentMeta = useMemo(
    () => pageMeta[location.pathname] || pageMeta["/dashboard/home"],
    [location.pathname],
  );
  const pageTitle =
    location.pathname === "/dashboard/home"
      ? `${getGreeting()}, ${getDisplayName(user)}`
      : currentMeta.title;

  useEffect(() => {
    let cancelled = false;

    const keepSessionAlive = async () => {
      const currentToken = Cookies.get("jwtToken");
      if (!isTokenExpiringSoon(currentToken, 300)) {
        return;
      }

      try {
        await refreshAuthSession();
      } catch (error) {
        if (!cancelled) {
          clearAuthCookies();
          navigate("/");
        }
      }
    };

    keepSessionAlive();
    const interval = setInterval(keepSessionAlive, 60000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [navigate]);

  return (
    <div className="ax-shell">
      <aside className="ax-sidebar">
        <SidebarContent location={location} navigate={navigate} user={user} />
      </aside>

      <Drawer
        anchor="left"
        open={mobileNavOpen}
        onClose={() => setMobileNavOpen(false)}
        PaperProps={{
          sx: {
            width: 260,
            background: "#0a0a0a",
            color: "rgba(255,255,255,0.82)",
            p: 1.5,
            borderRight: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          },
          className: "ax-mobile-sidebar-paper",
        }}
      >
        <SidebarContent
          location={location}
          navigate={navigate}
          user={user}
          onNavigate={() => setMobileNavOpen(false)}
        />
      </Drawer>

      <div className="ax-main">
        <IconButton
          className="ax-mobile-toggle"
          onClick={() => setMobileNavOpen(true)}
          aria-label="Open navigation"
        >
          <MenuRoundedIcon fontSize="small" />
        </IconButton>

        <main className="ax-content">
          <div
            className={`ax-page-heading${
              location.pathname === "/dashboard/home" ? " ax-home-heading" : ""
            }`}
          >
            <h1>{pageTitle}</h1>
            <p>{currentMeta.subtitle}</p>
          </div>

          <Outlet />
        </main>
      </div>
    </div>
  );
}
